import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import moment from 'moment';

import { RecentOrFavoriteOrder, RecentOrFavoriteOrderMeal, Menu, MenuItemLookupResult, OrderType, OrderErrorCode, OrderApiException, OrderWithContext, BYOMealType } from '@/models';
import { joinWithSuffix } from '@/helpers/formatting';
import { findEntreeInMenu, isMealBYO } from '@/helpers/menu';
import { AnalyticsEventCategory, AnalyticsEventAction } from '@/services/google/google-analytics.services';
import flatMap from 'lodash/flatMap';
import { AccountManagementEvent, AddToBagEvent } from '@/services/analytics/events';
import { Routes, isGroupOrderRoute } from '@/router';
import { getAddToBagInteraction } from '@/services/analytics/platforms/adobe-analytics/helpers';
import { AddToBagWebInteraction } from '@/services/analytics/platforms/adobe-analytics/constants';
import { AdobeCartAddEvent } from '@/services/analytics/platforms/adobe-analytics/adobe-events';
import { AnalyticsMealType } from '@/services/analytics/platforms/adobe-analytics/models';
import { formatEntreeContents, joinItemsWithSuffix, joinWithSuffixOfMoreEntreeContents } from '@/helpers';
import { formatRecentEntreeName } from '@/services/recent-and-favorite-orders/recent-and-favorite-orders-helper';
import _ from 'lodash';
import { MaxEntreeErrorEvent } from '@/services/analytics/events/max-byoc-error-event';

const MAX_LENGTH_OF_DETAILS = 70;

@Component({
  name: 'CmgOrderCard',
})
export default class CmgOrderCard extends Vue {
  detailsTruncated = false;
  addingToBag = false;
  isOrderStart = false;

  @Prop({ required: true, type: Object })
  order!: RecentOrFavoriteOrder;

  @Prop({ required: true, type: Boolean })
  isFavorite!: boolean;

  @Prop({ default: '', type: String })
  uniqueId?: string;

  @Prop({ required: true, type: Boolean })
  showFavorites!: boolean;

  @Prop({ type: Number })
  linkPosition!: number;

  get isSingleEntree(): boolean {
    if (!this.meals || !this.meals.length) {
      return false;
    }

    // Remove non entree meals so we can verify that this is actually a single entree.
    const mealsWithEntrees = this.meals.filter(meal => meal.entrees && !!meal.entrees.length);

    // checking that there is one meal and that it has an entree.
    return mealsWithEntrees.length === 1 && !!this.getEntreeNameFromMenu(mealsWithEntrees[0]);
  }

  get meals(): RecentOrFavoriteOrderMeal[] {
    return this.order.order.meals;
  }

  get buttonText(): string {
    return 'Add To Bag';
  }

  get hasSidesOrDrinksOnly(): boolean {
    const meals = this.meals;

    return !!meals.find(meal => {
      const hasEntrees = meal.entrees && !!meal.entrees.length;

      return !this.mealHasUpSellItems(meal) && !hasEntrees && ((meal.sides && !!meal.sides.length) || (meal.drinks && !!meal.drinks.length));
    });
  }

  get menu(): Menu | null {
    return this.$appStore.state.menu.activeMenu;
  }

  get menuItemLookupMap(): Map<string, MenuItemLookupResult> | null {
    return this.$appStore.state.menu.menuItemLookupMap;
  }

  get countryCode(): string {
    return this.$appStore.state.ui.countryProfile.countryCode;
  }

  get formattedOrderHeaderContent(): string {
    if (this.showFavorites) {
      return this.order.favoriteName;
    }

    return moment().isSame(this.order.orderDateTime, 'day') ? 'Today' : (this.countryCode === 'GB' ? moment(this.order.orderDateTime).format('DD/MM/YYYY') : moment(this.order.orderDateTime).format('MM/DD/YYYY'));
  }

  get formattedOrderForText(): string {
    const meal = this.meals[0];
    if (!meal) {
      return '';
    }

    return '';
  }

  get formattedOrderEntreeName(): string {
    const meal = this.meals.find(m => m.entrees && m.entrees.length);
    if (!meal) {
      return '';
    }

    if (meal.preconfiguredMealName && !meal.wasMealModified) {
      return meal.preconfiguredMealName;
    }

    return this.isSingleEntree ? formatRecentEntreeName(meal.entrees[0], this.menuItemLookupMap) : '';
  }

  get formattedOrderEntreeDetails(): string {
    const entree = this.meals.find(m => m.entrees && m.entrees.length)?.entrees[0];

    if (!entree) {
      return '';
    }

    let items = formatEntreeContents(entree, this.menuItemLookupMap);

    const drinkItems = flatMap(this.meals, m => flatMap(m.drinks, d => d.menuItemName));
    const sideItems = flatMap(this.meals, m => flatMap(m.sides, s => s.menuItemName));

    items = items.concat(drinkItems).concat(sideItems);

    const suffix = items.length == 2 ? ' and ' : ', and ';

    if (items.join(', ').length > MAX_LENGTH_OF_DETAILS) {
      this.detailsTruncated = true;
      return joinWithSuffixOfMoreEntreeContents(items, MAX_LENGTH_OF_DETAILS);
    }

    return joinWithSuffix(items, ', ', suffix);
  }

  get formattedOrderDetails(): string {
    const meals = this.meals?.filter(meal => !this.mealHasUpSellItems(meal)).map(m => this.getEntreeNameFromMenu(m));

    const suffix = meals.length == 2 ? ' and ' : ', and ';

    if (meals.join(', ').length > MAX_LENGTH_OF_DETAILS) {
      this.detailsTruncated = true;
      return joinWithSuffixOfMoreEntreeContents(meals, MAX_LENGTH_OF_DETAILS);
    }

    return joinWithSuffix(meals, ', ', suffix);
  }

  get favoriteIconUrl(): string {
    return this.isFavorite ? this.$appStore.state.cms.images.icons.heartFilled.default : this.$appStore.state.cms.images.icons.heartEmpty.default;
  }

  get favoriteLabel(): string {
    return this.isFavorite ? 'Remove the Favorite' : 'Make it Favorite';
  }

  // If a meal has no entrees, we want to get a count of
  // the sides and display them in a comma separated list.
  get formattedSidesOnlyDetails(): string {
    const sidesAndDrinks = this.meals.reduce((acc: { menuItemName: string; quantity: number }[], meal) => {
      return acc.concat(meal.sides || []).concat(meal.drinks || []);
    }, []);

    const sidesMap = new Map<string, number>();
    let lengthOfResult = 0;

    for (const item of sidesAndDrinks) {
      const sidesCount = sidesMap.get(item.menuItemName) || 0;

      lengthOfResult += item.menuItemName.length + 3;
      sidesMap.set(item.menuItemName, sidesCount + item.quantity);
    }

    if (lengthOfResult >= MAX_LENGTH_OF_DETAILS) {
      this.detailsTruncated = true;

      return this.joinWithSuffixOfMoreSideItems(sidesMap, MAX_LENGTH_OF_DETAILS);
    }

    const detailsList = Array.from(sidesMap.entries()).map(entry => {
      return `${entry[1]} ${entry[0]}`;
    });

    return joinWithSuffix(detailsList, ', ', ', and ');
  }

  get shouldShowFullDetails(): boolean {
    return this.detailsTruncated || !this.isSingleEntree;
  }

  mealHasUpSellItems(meal: RecentOrFavoriteOrderMeal): boolean {
    return !!meal.drinks?.find(d => d.isUpSell) || !!meal.sides?.find(s => s.isUpSell);
  }

  async onClickAddToBag(showLoader: boolean = true) {
    /*
     * The flow here is a bit weird, but the main gist is that we need to either get or create an order
     * which means the whole thing reads a bit out of order and uses continuations to make everything work
     */

    if (showLoader) {
      this.addingToBag = true;
    }

    let order = this.$services.order.getCurrentOrder();
    try {
      if (!order) {
        // Need to create order
        const restaurant = this.$appStore.state.restaurant.selectedRestaurant;
        if (!restaurant) {
          return;
        }
        order = await this.$services.order.createOrder(restaurant.restaurantNumber, OrderType.Regular);
        this.isOrderStart = true;
      } else {
        this.isOrderStart = false;
      }

      // if there's still no order, bail out.
      if (!order) {
        return;
      }

      if (isGroupOrderRoute(this.$route)) {
        for (const meal of this.order.order.meals) {
          await this.$services.order.duplicateRecentsAndFavsMeal(order, meal);
          order = this.$services.order.getCurrentOrder();

          if (!order) {
            return; // Something went horribly wrong if we get here
          }
        }

        this.$router.push({ name: Routes.GroupOrderConfirmation });
      } else {
        await this.$services.order.copyMeals(this.order.orderId, order);

        this.$services.bag.setBagVisibility(true);
        this.$nextTick(() => {
          Vue.nextTick(() => this.$services.ui.focusDiv('.fullModal'));
          this.$services.ui.setLastSelectedElement('bag');
        });
      }
    } catch (e: any) {
      if (e instanceof OrderApiException && (e.errors[0].errorCode == OrderErrorCode.MaxEntreeLimitExceeded || e.errors[0].errorCode == OrderErrorCode.MaxBYOLimitExceeded)) {
        const byoEntrees = this.order?.order.meals?.find(m => isMealBYO(m, this.menu))?.entrees;
        const preconfiguredItemName = byoEntrees && byoEntrees.length > 0 ? byoEntrees[0].preconfiguredItemName ?? byoEntrees[0].menuItemName : '';
        this.$services.analytics.send(new MaxEntreeErrorEvent(
          e.errors[0], 
          preconfiguredItemName,
          this.$router.currentRoute.name ?? '',
          e.errors[0].errorCode == OrderErrorCode.MaxBYOLimitExceeded ? BYOMealType.BYOProtein : BYOMealType.BYOChips
        ));
        
        this.$services.ui.setModalVisibility('maxEntreeLimitModal', true, { errorMessage: e.errors[0].errorData, errorCode: e.errors[0].errorCode });
      } else {
        this.$services.logger.error('Something went wrong while adding recent/favorite to bag', undefined, e);
        this.$services.ui.setModalVisibility('globalErrorToast', true);
      }
    } finally {
      if (showLoader) {
        this.addingToBag = false;
      }
    }

    const eventAction = this.isFavorite ? AnalyticsEventAction.AddToBagFavorite : AnalyticsEventAction.AddToBagRecent;
    this.$services.googleAnalytics.sendCustomEvent({ category: AnalyticsEventCategory.Reorder, action: eventAction });
    // Trigger GA event for adding all contents to bag
    this.meals.forEach(meal => {
      this.$services.googleAnalytics.recentOrFavoriteMealAddedToBag(meal);
    });
    if (order) {
      this.sendAdobeAddToBagEvents(order, this.isOrderStart);
    }
  }

  onClickFavoriteToggle() {
    this.$emit('toggle-favorite', {});
  }

  onClickShowFullDetails(event: any) {
    const orderTimestamp = moment(this.order.orderDateTime);
    const formattedOrderTimestamp = moment().diff(orderTimestamp, 'days') == 0 ? 'Today' : orderTimestamp.format('MM/DD/YYYY');
    this.$services.ui.pushEventTargetAsLastSelectedElement(event);
    this.$services.ui.setModalVisibility('fullOrderDetails', true, {
      order: this.order.order,
      name: this.order.favoriteName || formattedOrderTimestamp,
      onClickAddToBag: () => this.onClickAddToBag(false),
    });
    this.$services.ui.firstFocus();
    this.$services.analytics.send(new AccountManagementEvent(`${this.isFavorite ? 'favoritedOrdersViewed' : 'recentOrdersViewed'}`, this.order.order.meals))
  }

  getEntreeNameFromMenu(meal: RecentOrFavoriteOrderMeal): string {
    const entrees = meal && meal.entrees;

    if (!this.menu || !entrees) {
      return '';
    }

    for (const entree of entrees) {
      const menuEntree = findEntreeInMenu(entree.menuItemId, this.menu);
      if (menuEntree) {
        return menuEntree.itemName;
      }
    }

    return '';
  }

  // When a list of sides is too long for a given view we can append
  // 'and <count> more items' to the string.
  joinWithSuffixOfMoreSideItems(items: Map<string, number>, maxChars: number): string {
    const mappedItems = [];
    for (const entry of items.entries()) {
      mappedItems.push({
        name: entry[0],
        quantity: entry[1],
      });
    }

    return joinItemsWithSuffix(mappedItems, maxChars, true);
  }

  private sendAdobeAddToBagEvents(order: OrderWithContext, isOrderStart: boolean): void {
    const currentOrderMeals = this.$services.order.getCurrentOrder()?.order.meals;
 
    let mealsAddedToBag;
    if (order?.order.meals && currentOrderMeals) {
      mealsAddedToBag = _.differenceWith(currentOrderMeals, order?.order.meals, (meal1, meal2) => meal1.mealId === meal2.mealId);
    }
    
    const linkSection = this.$router.currentRoute.name == Routes.Home ? 'recent-and-favorite-orders-mini' : 'recent-and-favorite-orders-page';
    const linkTitle = this.showFavorites ? 'favorites' : 'recents';
    this.$services.analytics.addLinkClickedEventToStore(getAddToBagInteraction(AddToBagWebInteraction, linkTitle, this.linkPosition, linkSection));
    this.$services.analytics.setAddToBagEventToStore(new AdobeCartAddEvent(new AddToBagEvent(mealsAddedToBag ?? currentOrderMeals ?? [], this.showFavorites ? AnalyticsMealType.Favorite : AnalyticsMealType.Recent, isOrderStart, undefined, false, this.isFavorite ? 'favorites-reorder' : 'recents-reorder'),  this.$appStore, this.$services));
  }
}
